import type { ClientHost } from '../../client/mode';
import type { Player, SessionView, SessionSummary, PublicTurn, GuessResult, GuessEvent, GuessViewEvent, GuessProcess, StrokeBrush, StrokeShape } from './types';
import { BASE_COLORS, CHOICE_REFRESH, GUESS_EVENT_LIMIT, GUESS_EVENT_MAX_MS } from './types';
import { DrawingEditor, renderDrawing, renderDrawingProgress, replayDuration, emptyDrawing, setStrokeCanvasFactory, STROKE_DEFAULT_WIDTH, SHAPE_FILL_OPACITY, DRAG_BRUSHES } from './drawing';
import type { Drawing } from './types';
import { type Pointer, type Hit, requestId } from '../../client/platform';
import { C, T } from '../../client/paint';
import { renderDrawingTools, renderBrushCatalog, brushCatalogBox, snappedLine, selectDrawingBrush, renderDragPreview } from '../../client/drawing-tools';
import { drawBrushSample } from '../../client/brush-sample';
import { BRUSH_SIZES, brushWidthForRatio } from '../../shared/brush';
import { CanvasView } from '../../client/canvas-view';
import { loadDraft, storeDraft, removeDraft, draftFailureMessage } from '../../client/drawing-draft';
import { DrawingEditorRenderer } from '../../client/drawing-editor-renderer';
import { readGuessRecording, type GuessRecording } from './guess-recording';
import { guessViewAt } from './guess-view';

declare const __VERSION__: string;
import type { Screen } from '../../client/mode';

export class DrawGuessClient {
  session: SessionView | null = null;
  editor = new DrawingEditor();
  practice = false;
  color = BASE_COLORS[0];
  width = 12;
  eraserWidth = 26;
  eraser = false;
  /** 当前笔头；橡皮是独立开关，退出橡皮会回到 lastBrush，不会把用户选的笔头弄丢。 */
  brush: StrokeBrush = 'pen';
  lastBrush: StrokeBrush = 'pen';
  brushPanel = false;
  /** 形状笔头当前的形状与空心 / 实心（在同一支笔头里循环切换，不额外占界面）。 */
  shape: StrokeShape = 'circle';
  shapeFilled = false;
  /** 直线笔头：按下起点 / 当前终点（逻辑坐标）；抬手才落笔，中途取消不留痕迹。 */
  lineOptions?: ReturnType<DrawGuessClient['strokeOptions']>;
  lineFrom: { x: number; y: number } | null = null;
  lineTo: { x: number; y: number } | null = null;
  /** 这一串手势已经被弹层 / 滑块消费掉：后续 move / up 不再落到画布上。 */
  gestureConsumed = false;
  /** 每个笔头各自记粗细；橡皮仍用 eraserWidth。 */
  brushWidths: Record<StrokeBrush, number> = { ...STROKE_DEFAULT_WIDTH };
  brushSliding: number | null = null;
  pendingTouchHit: Hit | null = null;
  brushSizes = [...BRUSH_SIZES];
  palette = 0;
  pointerId: number | null = null;
  readonly view = new CanvasView();
  get zoom() { return this.view.zoom; }
  set zoom(value: number) { this.view.zoom = value; }
  get zoomPan() { return this.view.pan; }
  set zoomPan(value: { x: number; y: number }) { this.view.pan = value; }
  board = { x: 24, y: 142, size: 342 };
  panel = { y: 496, height: 108 };
  /** 粗细滑块那一行的 y（面板内坐标）；绘制与命中都读它，避免两处常量漂移。 */
  sliderY = 530;
  /** 滑块轨道右端：跟着最大档手柄半径走，保证手柄不顶到卡片边。 */
  sliderRight = 348;
  /** 笔头目录弹层：盖在画板下半部，不占布局、不改抽屉高度。 */
  brushPanelBox = { y: 300, height: 176 };
  buffer: HTMLCanvasElement;
  bufferRevision = -1;
  private editorRenderer: DrawingEditorRenderer;
  private draftKey = '';
  private savedDraftEditor?: DrawingEditor;
  private savedDraftRevision = -1;
  private draftTimer?: ReturnType<typeof setTimeout>;
  private draftFailed = false;
  private unsavedDrafts = new Map<string, DrawingEditor>();
  replayStarted = 0;
  replaySpeed = 1;
  replayFinished = false;
  replayDrawing: Drawing | null = null;
  replayRenderedFull = false;
  private replayPausedPosition: number | null = null;
  private processDrawing: Drawing | null = null;
  private processDrawingPosition = -1;
  thumbnails = new Map<string, HTMLCanvasElement>();
  thumbnailLoading = new Set<string>();
  thumbnailFailed = new Set<string>();
  answer = '';
  picks: number[] = [];
  typed = false;
  /** 猜题过程记录：本回合首次进入猜词页后的操作流水，随每次提交上报给服务端（确认后清空，避免重复）。 */
  guessLog: GuessEvent[] = [];
  guessViewLog: GuessViewEvent[] = [];
  /** 流水的时间基准：清空 guessLog 时保留它，事件时间因此始终单调递增。 */
  guessLogAt = 0;
  private guessLastAt = 0;
  private guessRecordKey = '';
  private guessRecordFailed = false;
  private guessRecords = new Map<string, GuessRecording>();
  /** 猜题回放：正在回看的回合与过程数据（事件流单独取自 /guess-replay）。 */
  processTurn: PublicTurn | null = null;
  guessProcess: GuessProcess | null = null;
  processReturn: Screen = 'session';
  processPlaying = false;
  processSpeed = 1;
  processElapsed = 0;
  processStartedAt = 0;
  processAt: number[] = [];
  processAtFor = '';
  processDuration = 0;
  /** 逐步查看可以停在同一毫秒内的任意一步；正常播放始终按真实时间推进。 */
  processStep: number | null = null;
  /** 回放页里画作与候选字块的实际矩形（绘制与命中都读它，测试也不必复刻公式）。 */
  processBoard = { x: 24, y: 111, size: 300 };
  processTiles = { y: 0, height: 0 };
  get usedPalette() { return this.host.paletteSelection.id; }
  set usedPalette(id: string) { this.host.paletteSelection.id = id; }
  shopTier = 0;
  result: GuessResult | null = null;
  /** 等待页「与 TA 一起画过的画」：会话视图只带最近 12 幅，完整列表经 /sessions/:id/history 懒加载；
   *  historyNeed 是「应该加载」的 key（会话 + 最新已揭晓回合），historyGot 是 historyItems 实际对应的 key。 */
  historyNeed = '';
  historyGot = '';
  historyItems: PublicTurn[] = [];
  historyPage = 0;
  mailAt = 0;
  /** 结果页邮戳盖章（含彩纸）：进入结果页的时刻。 */
  stampAt = 0;
  /** 猜词：最近一次选中候选字（答案格弹跳）与当前批次进场（字块交错滑入）。 */
  pickAt = 0;
  batchAt = 0;
  /** 星星罐：最近一次余额变化（罐子弹跳 + 闪光）。 */
  sliderRect() { return { x0: 94, x1: this.sliderRight, y: this.sliderY }; }
  setBrushFromX(x: number) {
    const s = this.sliderRect(); const ratio = Math.max(0, Math.min(1, (x - s.x0) / (s.x1 - s.x0)));
    // 粗细按笔头分别记忆：滑块调的是当前这支笔（橡皮是独立开关，用它自己的档位）。
    if (this.eraser) this.eraserWidth = brushWidthForRatio(ratio); else { this.width = brushWidthForRatio(ratio); this.brushWidths[this.brush] = this.width; }
    this.host.drawingSettings.width(this,this.eraser?this.eraserWidth:this.width);
  }
  pendingReplay(): PublicTurn | undefined {
    // A reply is the latest own round, not a queue of all unseen historical replays.
    const view = this.session; if (!view || !this.host.player) return undefined;
    const latest = [view.current, ...view.history]
      .filter(turn => turn.status === 'resolved' && turn.artistId === this.host.player!.id)
      .sort((a, b) => b.number - a.number)[0];
    return latest?.hasReplay && !this.host.platform.get(this.replaySeenKey(latest.id), false) ? latest : undefined;
  }
  /** 最近一次「我画的画、被 TA 猜过」的回合：用于手动回看入口，不受是否看过限制。 */
  latestOwnReplay(): PublicTurn | undefined {
    const view = this.session; if (!view || !this.host.player) return undefined;
    return [view.current, ...view.history]
      .filter(turn => turn.status === 'resolved' && turn.artistId === this.host.player!.id && turn.hasReplay)
      .sort((a, b) => b.number - a.number)[0];
  }
  replaySeenKey(turnId: string) { return `guessReplay:${turnId}`; }
  startProcess() { this.processElapsed = 0; this.processStartedAt = Date.now(); this.processPlaying = true; this.processSpeed = 1; this.processStep = null; this.processDrawing = null; }
  /** 播完之后从哪来回哪去；回到会话页时顺手刷新一次，把新回合拉下来。 */
  leaveProcess() {
    const back = this.processReturn;
    this.guessProcess = null; this.processTurn = null; this.processPlaying = false; this.processElapsed = 0;
    this.host.screen = back;
    if (back === 'session' && this.session) void this.host.run(() => this.host.refresh());
  }
  /** 拉取一次猜题过程并开播；看过的回合记在本地，之后不再自动播放（仍可手动回看）。 */
  async loadGuessReplay(turn: PublicTurn, returnTo: Screen, isCurrent = () => true) {
    const process = await this.host.api<GuessProcess>(`/turns/${turn.id}/guess-replay`);
    if (!isCurrent()) return;
    this.guessProcess = process; this.processTurn = turn; this.processReturn = returnTo;
    this.processAt = []; this.processAtFor = ''; this.processDuration = 0;
    // 画作与作答使用同一个猜题时钟，不能把画作提前强制画完整。
    this.replayRenderedFull = false; this.replayDrawing = null;
    this.startProcess();
    this.host.platform.set(this.replaySeenKey(turn.id), true);
    this.host.screen = 'replay';
  }
  draw(practice = false) {
    if (this.host.screen === 'draw') this.pause();
    const draftKey = practice ? 'practice' : `draft:${this.session!.current.id}`;
    try {
      this.editor = this.unsavedDrafts.get(draftKey) ?? new DrawingEditor(loadDraft(this.host.platform, draftKey) ?? (practice ? emptyDrawing() : this.session?.current.drawing ?? emptyDrawing()));
    } catch { this.host.toast('草稿未能完整读取，请重新打开画纸。'); return; }
    this.practice = practice; this.draftKey = draftKey;
    this.savedDraftEditor = undefined; this.savedDraftRevision = -1; this.draftFailed = this.unsavedDrafts.has(draftKey);
    this.bufferRevision = -1; this.view.reset(); this.host.screen = 'draw';
  }
  saveDraft(immediate = true) {
    if (this.host.screen !== 'draw') return;
    if (this.draftTimer !== undefined) { clearTimeout(this.draftTimer); this.draftTimer = undefined; }
    if (this.savedDraftEditor === this.editor && this.savedDraftRevision === this.editor.revision) return;
    // Pointer callbacks only schedule work. Serialize after a short pause, never in the middle of a gesture.
    if (!immediate) {
      this.draftTimer = setTimeout(() => { this.draftTimer = undefined; this.saveDraft(this.pointerId === null); }, 500);
      return;
    }
    const key = this.draftKey || (this.practice ? 'practice' : `draft:${this.session!.current.id}`);
    try {
      storeDraft(this.host.platform, key, this.editor.document);
      this.savedDraftEditor = this.editor; this.savedDraftRevision = this.editor.revision;
      this.unsavedDrafts.delete(key); this.draftFailed = false;
    } catch (error) {
      this.unsavedDrafts.set(key, this.editor);
      if (!this.draftFailed) this.host.toast(draftFailureMessage(error));
      this.draftFailed = true;
    }
  }
  replay(record = true) {
    this.replayStarted = Date.now(); this.replayFinished = false; this.replaySpeed = 1; this.replayDrawing = null; this.replayRenderedFull = false; this.replayPausedPosition = null;
    if (record && this.host.screen === 'guess') this.logGuessView();
  }
  finishReplay() {
    this.replayFinished = true; this.replayRenderedFull = false; this.replayDrawing = null; this.replayPausedPosition = null;
    if (this.host.screen === 'guess') this.logGuessView();
  }
  replaySpeedLabel() { return `${Math.max(1, Math.round(this.replaySpeed))} 倍速`; }
  guess() {
    this.answer = ''; this.picks = []; this.typed = false; this.replay(false); this.batchAt = Date.now(); this.beginGuessLog();
    const drawing = this.session?.current.drawing; if (drawing) this.replaySpeed = Math.max(1, replayDuration(drawing) / 10000);
    this.host.screen = 'guess'; this.logGuessView();
  }
  /** 同一设备重新进入时沿用首次进入时间，包含离开期间的真实等待。 */
  beginGuessLog() {
    this.guessRecordKey = `guess-recording:${this.host.player?.id ?? ''}:${this.session!.current.id}`;
    let record = this.guessRecords.get(this.guessRecordKey);
    try { record ??= readGuessRecording(this.host.platform.get(this.guessRecordKey, null)); } catch { /* 内存中的过程仍可继续。 */ }
    this.guessLog = record?.events.slice() ?? []; this.guessLogAt = record?.startedAt ?? Date.now(); this.guessLastAt = record?.lastAt ?? 0;
    this.guessViewLog = record?.viewEvents?.slice() ?? [];
    this.answer = record?.answer ?? ''; this.picks = record?.picks.slice() ?? []; this.typed = record?.typed ?? false;
    if (this.session!.current.hintUsed && !record?.hintUsed) { this.logGuess('hint'); this.clearAnswer(); }
    this.saveGuessLog();
  }
  private saveGuessLog() {
    if (!this.guessRecordKey) return;
    const record: GuessRecording = { version: 1, startedAt: this.guessLogAt, lastAt: this.guessLastAt, events: this.guessLog.slice(),
      viewEvents: this.guessViewLog.slice(),
      answer: this.answer, picks: this.picks.slice(), typed: this.typed, hintUsed: Boolean(this.session?.current.hintUsed) };
    this.guessRecords.set(this.guessRecordKey, record);
    try { this.host.platform.set(this.guessRecordKey, record); this.guessRecordFailed = false; }
    catch { if (!this.guessRecordFailed) this.host.toast('猜题过程暂未保存到本机，请尽量在本次完成。'); this.guessRecordFailed = true; }
  }
  logGuess(kind: GuessEvent['kind'], extra: Omit<GuessEvent, 'kind' | 't'> = {}) {
    if (!this.guessLogAt || this.guessLog.length >= GUESS_EVENT_LIMIT) return;
    this.guessLastAt = Math.min(GUESS_EVENT_MAX_MS, Math.max(this.guessLastAt, Date.now() - this.guessLogAt));
    this.guessLog.push({ kind, ...extra, t: this.guessLastAt });
    // 操作节点也保存画作快照，断线／恢复后仍能定位提交时真正看到哪一笔。
    this.logGuessView(this.guessLastAt, false);
    this.saveGuessLog();
  }
  guessDrawingPosition(now: number) {
    const drawing = this.session?.current.drawing; const end = drawing ? replayDuration(drawing) : 0;
    return Math.min(end, Math.max(0, this.replayFinished ? end : this.replayPausedPosition ?? (now - this.replayStarted) * this.replaySpeed));
  }
  logGuessView(t?: number, persist = true) {
    if (!this.guessRecordKey || !this.session?.current.drawing || this.guessViewLog.length >= GUESS_EVENT_LIMIT) return;
    const now = Date.now(); this.guessLastAt = t ?? Math.min(GUESS_EVENT_MAX_MS, Math.max(this.guessLastAt, now - this.guessLogAt));
    const position = this.guessDrawingPosition(now);
    this.guessViewLog.push({ t: this.guessLastAt, position, speed: this.replaySpeed,
      playing: this.replayPausedPosition === null && !this.replayFinished && position < replayDuration(this.session.current.drawing) });
    if (persist) this.saveGuessLog();
  }
  /** 选中当前这一位的候选字，然后自动跳到下一批；拼好的答案由 picks 逐位映射得到。 */
  pickLetter(batch: string[], index: number) {
    // 自己输入的答案被候选字替换掉也是一次「取消」，回放里要能看见。
    if (this.typed) this.clearAnswer();
    const banks = this.session!.current.banks ?? [];
    const slot = this.picks.length;
    this.picks.push(index);
    this.answer = this.picks.map((tile, position) => banks[position][tile]).join('');
    this.logGuess('pick', { slot, index });
    this.pickAt = Date.now(); this.batchAt = this.pickAt;
  }
  clearAnswer() {
    // 有内容被清掉才算一次「取消选择」，回放里要能看到这一步。
    const changed = Boolean(this.picks.length || this.answer);
    this.answer = ''; this.picks = []; this.typed = false; this.batchAt = Date.now();
    if (changed) this.logGuess('clear');
  }
  submitGuess(giveUp = false) {
    void this.host.run(async () => {
      if (giveUp && !await this.host.platform.confirm('揭晓答案？', '这一回合不会获得星星，接下来轮到你画。')) return;
      this.logGuess('submit', { text: this.answer, giveUp });
      const events = this.guessLog.slice(); const viewEvents = this.guessViewLog.slice();
      const value = await this.host.api<GuessResult>(`/turns/${this.session!.current.id}/guess`, 'POST', { answer: this.answer, giveUp, events, viewEvents });
      this.session = value.session; this.host.player = value.player;
      // 没猜中：这一段流水服务端已经收下，清空后续记；时间基准保留，事件时间继续递增。
      if (!value.resolved) {
        // 请求期间仍可能切后台；只确认本次真正发出的部分，保留后来新增的暂停节点。
        this.guessLog = this.guessLog.slice(events.length); this.guessViewLog = this.guessViewLog.slice(viewEvents.length);
        this.clearAnswer(); this.host.toast('还差一点点，再看看画里的线索。'); return;
      }
      this.guessRecords.delete(this.guessRecordKey);
      try { this.host.platform.remove(this.guessRecordKey); } catch { /* 已揭晓回合不会再进入猜题。 */ }
      this.guessRecordKey = '';
      this.result = value; this.host.screen = 'result'; this.stampAt = Date.now();
      if (value.correct && this.host.platform.get('vibration', true)) this.host.platform.vibrate();
    });
  }
  async exportDrawing(drawing: Drawing) {
    const canvas = this.host.platform.createCanvas(1000, 1000);
    renderDrawing(canvas.getContext('2d')!, drawing, 1000); await this.host.platform.export(canvas); this.host.toast('作品已保存。');
  }
  navigateGallery(dir: 1 | -1 = 1) {
    this.host.gallery.navigate(dir);
  }
  /** 作品缩略图（384px 离屏缓存，作品集与等待页的历史画作共用）；缓存上限 12 幅，最旧的先淘汰。 */
  thumbnailFor(turn: PublicTurn) {
    let thumbnail = this.thumbnails.get(turn.id);
    if (!thumbnail && !turn.drawing) {
      // Only visible cards call this helper; limit background requests to two, without blocking navigation.
      if (this.thumbnailLoading.size < 2 && !this.thumbnailLoading.has(turn.id) && !this.thumbnailFailed.has(turn.id)) {
        this.thumbnailLoading.add(turn.id);
        void this.host.api<{ drawing: Drawing }>(`/turns/${turn.id}/artwork?preview=1`).then(value => {
          turn.drawing = value.drawing;
          this.thumbnailFor(turn);
        }).catch(() => { this.thumbnailFailed.add(turn.id); })
          .finally(() => { this.thumbnailLoading.delete(turn.id); });
      }
      return undefined;
    }
    if (!thumbnail) {
      thumbnail = this.host.platform.createCanvas(384, 384);
      renderDrawing(thumbnail.getContext('2d')!, turn.drawing!, 384);
      if (this.thumbnails.size >= 12) this.thumbnails.delete(this.thumbnails.keys().next().value!);
      this.thumbnails.set(turn.id, thumbnail);
    }
    return thumbnail;
  }
  /** 打开作品回放页；returnTo 决定「回到作品集 / 回到画纸」按钮的去向。 */
  openArtwork(turn: PublicTurn, returnTo: Screen) {
    this.host.gallery.openLegacy(turn, returnTo);
  }
  async share() {
    if (!this.session?.inviteCode) return;
    if (this.session.inviteExpiresAt! < Date.now()) this.session = await this.host.api(`/sessions/${this.session.id}/invite`, 'POST', {});
    await this.host.platform.share(this.session!.inviteCode!);
  }
  async copyInvite() {
    if (!this.session?.inviteCode) return;
    if (this.session.inviteExpiresAt! < Date.now()) this.session = await this.host.api(`/sessions/${this.session.id}/invite`, 'POST', {});
    const code = this.session!.inviteCode!;
    if (!this.host.platform.copyText) { this.host.toast('当前环境暂不支持复制，请手动记下邀请码。'); return; }
    await this.host.platform.copyText(code);
    this.host.toast('邀请码已复制。');
  }
  pointer(kind: 'down' | 'move' | 'up' | 'cancel', point: Pointer) {
    if (kind === 'up' || kind === 'cancel') this.host.paint.pressedId = null;
    if (kind === 'cancel') this.pendingTouchHit = null;
    const local = point; const { x, y } = local;
    const b = this.board; const inside = this.view.contains(b, local);
    // A sole native touchstart is a new gesture even if an earlier release was
    // interrupted. Finish old ink without joining it to this new stroke.
    if (kind === 'down' && point.touches?.length === 1 && point.touches[0].id === point.id) {
      if (this.pointerId !== null) this.finishStroke(true);
      this.brushSliding = null; this.gestureConsumed = false; this.pendingTouchHit = null;
    }
    // Only fingers that start in the viewport belong to its gesture. A finger
    // adjusting a tool must never become a pinch finger.
    if (!this.host.busy && !this.brushPanel && this.brushSliding === null && !this.gestureConsumed) {
      const gesture = this.view.pointer(kind, local, b);
      if (gesture.started) {
        this.editor.cancelStroke(); this.cancelLine(); this.pointerId = null;
        this.pendingTouchHit = null; this.host.paint.pressedId = null; this.saveDraft(false);
      }
      if (gesture.consumed) return;
    }
    if (this.host.screen === 'draw' && !this.host.busy && this.pointerId === null) {
      // 滑块条所在的那一行整体归滑块所有：拖动和点按都只调粗细，不会误触工具圆钮。
      const s = this.sliderRect(); const onSlider = x >= 24 && x <= this.host.W - 24 && y >= s.y - 22 && y <= s.y + 22;
      if (kind === 'down' && onSlider && this.brushSliding === null) { this.brushSliding = point.id; this.gestureConsumed = true; this.setBrushFromX(x); return; }
      if (this.brushSliding === point.id && ['move', 'up', 'cancel'].includes(kind)) {
        if (kind !== 'cancel') this.setBrushFromX(x);
        if (kind !== 'move') { this.brushSliding = null; this.gestureConsumed = false; }
        return;
      }
      if (this.brushSliding !== null) return;
      // 笔头目录打开时它是模态的：命中区优先于画布，点到面板外就关掉，绝不在纸上落笔。
      // 这一整串手势都算被弹层吃掉（gestureConsumed），抬手前不会突然转到画布上。
      if (this.brushPanel && kind === 'down') {
        this.gestureConsumed = true;
        const inPanel = x >= 24 && x <= this.host.W - 24 && y >= this.brushPanelRect().y - 12 && y <= this.brushPanelRect().y + this.brushPanelRect().height;
        const hitPanel = [...this.host.paint.hits].reverse().find(h => !h.disabled && x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h);
        if (hitPanel) { this.brushPanel = false; hitPanel.action(); return; }
        if (inPanel) return;
        this.brushPanel = false;
      } else if (this.brushPanel) return;
    }
    // 弹层 / 滑块消费掉的手势：抬手时清标记，中间不落到画布上。
    if (this.gestureConsumed) { if (kind === 'up' || kind === 'cancel') this.gestureConsumed = false; return; }
    if (kind === 'down') {
      if (this.host.busy || this.pointerId !== null) return;
      if (this.host.screen === 'draw' && inside) {
        if (!this.view.containsPaper(b, local)) return;
        this.pointerId = point.id;
        const { x: logicalX, y: logicalY } = this.view.toDocument(b, local, true);
        this.beginStroke(logicalX, logicalY);
      } else {
        const hit = [...this.host.paint.hits].reverse().find(h => !h.disabled && x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h);
        if (hit) this.host.paint.pressedId = hit.id;
        if (hit?.id === 'send-drawing' && kind === 'down') { this.pendingTouchHit = hit; return; }
        hit?.action();
      }
    } else if (kind === 'up' && this.pendingTouchHit) {
      const hit = this.pendingTouchHit; this.pendingTouchHit = null; hit.action();
    } else if (this.pointerId === point.id) {
      // 手指滑出画布不断笔：坐标钳制到纸边继续画，抬笔才结束这一笔。
      if (kind === 'move' || kind === 'up') {
        const { x: logicalX, y: logicalY } = this.view.toDocument(b, local, true);
        this.extendStroke(logicalX, logicalY);
      }
      if (kind === 'up' || kind === 'cancel') this.finishStroke(kind === 'cancel');
    }
  }
  /** 当前笔头落笔用的参数：橡皮仍是独立开关，不受笔头影响；形状笔头带上形状与空心实心。 */
  strokeOptions() {
    return {
      color: this.color, width: this.eraser ? this.eraserWidth : this.brushWidths[this.brush],
      ...this.host.drawingSettings.stroke(this),
      brush: this.brush, mirror: this.brush === 'mirror',
      ...(this.brush === 'shape' ? { shape: this.shape, ...(!this.shapeFilled ? {} : { opacity: SHAPE_FILL_OPACITY }) } : {}),
      ...(this.eraser ? { tool: 'eraser' as const } : {}),
    };
  }
  /** 切换笔头：各自恢复上次用的粗细，并结束任何还没落笔的拖动预览。 */
  selectBrush(brush: StrokeBrush) {
    selectDrawingBrush(this.host, this, brush, () => { this.lastBrush = this.brush; this.cancelLine(); this.saveDraft(false); });
  }
  beginStroke(x: number, y: number) {
    this.brushPanel = false;
    if (!this.eraser && DRAG_BRUSHES.includes(this.brush)) { this.lineOptions=this.strokeOptions();this.lineFrom = { x, y }; this.lineTo = { x, y }; this.previewDrag(); return; }
    try { this.editor.start(x, y, this.strokeOptions(), Date.now()); }
    catch (error) { this.host.toast((error as Error).message); }
  }
  extendStroke(x: number, y: number) {
    if (this.lineFrom) { this.lineTo = { x, y }; this.previewDrag(); return; }
    this.editor.move(x, y, Date.now());
  }
  /**
   * 抬手：直线与形状在这里才真的落笔（点一下就抬手视作误触，不留痕迹）。
   * 直线按 8° 吸附；形状不吸附——圆按短边做正圆，方与三角按包围盒。
   */
  finishStroke(cancelled = false) {
    const from = this.lineFrom;
    if (from && this.lineTo) {
      const to = this.lineOptions?.brush === 'line' ? this.snappedLine(from, this.lineTo) : this.lineTo;
      const frozenOptions=this.lineOptions;this.cancelLine();
      if (!cancelled && Math.hypot(to.x - from.x, to.y - from.y) >= 6) {
        const options = frozenOptions ?? this.strokeOptions();
        try {
          this.editor.start(from.x, from.y, options, Date.now());
          this.editor.move(to.x, to.y, Date.now() + 1);
          this.editor.end();
        } catch (error) { this.host.toast((error as Error).message); }
      }
    } else {
      this.editor.end();
    }
    this.pointerId = null; this.saveDraft(false);
  }
  cancelLine() {
    if (!this.lineFrom && !this.lineTo) return;
    this.lineFrom = null; this.lineTo = null; this.lineOptions=undefined;this.editor.clearPreview();
  }
  /**
   * 直线吸附：手机上很难画直，离水平 / 垂直 / 45° 足够近就吸附过去（8° 容差）。
   * 吸附后预览画成实线，给一个「已经吸住了」的反馈。
   */
  snappedLine(from: { x: number; y: number }, to: { x: number; y: number }) {
    return snappedLine(from, to);
  }

  /** 拖动预览：直线按吸附结果、形状按包围盒；真正的落笔在抬手。 */
  previewDrag() {
    if (!this.lineFrom) return;
    const to = this.lineOptions?.brush === 'line' ? this.snappedLine(this.lineFrom, this.lineTo ?? this.lineFrom) : this.lineTo ?? this.lineFrom;
    this.editor.previewLine(this.lineFrom, to, this.lineOptions??this.strokeOptions());
  }
  /** 当前直线预览是否已经吸附（画成实线而不是虚线）。 */
  previewSnapped() {
    if (this.brush !== 'line' || !this.lineFrom || !this.lineTo) return false;
    const snapped = this.snappedLine(this.lineFrom, this.lineTo);
    return Math.hypot(snapped.x - this.lineTo.x, snapped.y - this.lineTo.y) > 0.5;
  }
  sessionScreen() {
    const s = this.session!; const turn = s.current; const p = this.host.paint; const me = turn.artistId === this.host.player!.id;
    const now = Date.now();
    this.host.header(`第 ${turn.number} 张画纸`, `共同猜中 ${s.solved} 幅`);
    if (s.status === 'closed') { p.wrap('这段友友会话已经结束。回到首页，开启新的画纸吧。', p.x(30), 150, p.span(330)); return; }
    let y = 108;
    if (s.inviteCode) {
      const invite = p.ctx.createLinearGradient(0, y, 0, y + 140);
      invite.addColorStop(0, '#FBE3D2'); invite.addColorStop(1, '#FDF3E2');
      p.box(p.x(24), y, p.span(342), 140, invite, 22, undefined, 18);
      p.tape(p.x(195), y + 2, 0.02, 64);
      p.text('把这张画纸寄给好友', p.x(42), y + 29, T.bodyStrong, C.ink, '600');
      // 邀请码印在齿孔邮票框内：像一张待寄出的信。
      p.ctx.font = `700 ${p.fontSize(18)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
      const codeW = Math.ceil(p.ctx.measureText(s.inviteCode).width);
      p.stamp(p.x(42), y + 44, codeW + 30, 38);
      p.text(s.inviteCode, p.x(42) + (codeW + 30) / 2, y + 63.5, T.cardTitle, C.orangeDeep, '700', 'center');
      p.button('copy-invite', '复制邀请码', p.x(42), y + 92, p.span(145), 38, () => { void this.host.run(() => this.copyInvite()); }, 'secondary', false, 'copy');
      p.button('share', '分享邀请', p.x(195), y + 92, p.span(145), 38, () => { void this.host.run(() => this.share()); }, 'orange', false, 'share'); y += 161;
    }
    const bottom = this.host.H - this.host.top - 68;
    if (me && turn.status === 'choosing') {
      p.text('挑一个想画的词', p.x(25), y + 13, T.hero, C.ink, '700'); p.text('画得简单一点，朋友也能懂。', p.x(26), y + 48, T.sub, C.mute); y += 79;
      turn.choices!.forEach((choice, i) => {
        const row = y + i * 78; const rot = [-0.012, 0.01, -0.008][i % 3];
        // 撕边词卡微微斜叠，左上角贴一小段胶带。
        p.ctx.save(); p.ctx.translate(this.host.W / 2, row + 33); p.ctx.rotate(rot); p.ctx.translate(-this.host.W / 2, -(row + 33));
        p.card(p.x(24), row, p.span(342), 66, 20);
        p.tape(p.x(48), row + 2, -0.35, 36, 12);
        p.text(choice.text, p.x(44), row + 25, T.title, C.ink, '600');
        p.ctx.restore();
        p.text(choice.category, p.x(44), row + 50, T.note, C.mute);
        p.ctx.font = `400 ${p.fontSize(11)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
        const catW = p.ctx.measureText(choice.category).width;
        for (let star = 0; star < choice.reward; star++) p.star(p.x(44) + catW + 16 + star * 13, row + 50, 5, C.gold);
        p.button(`choose-${choice.id}`, `+${choice.reward} 画它`, p.x(266), row + 15, p.span(82), 36, () => { this.host.platform.vibrate(); void this.host.run(async () => { this.session = await this.host.api(`/turns/${turn.id}/choose`, 'POST', { wordId: choice.id }); this.draw(); }); }, 'secondary', false, 'star', undefined, C.gold);
      });
      // 换一批题目：每回合前两次免费，之后每次消耗一颗星星（次数与价格直接写在按钮上）。
      const used = turn.refreshes ?? 0; const freeLeft = Math.max(0, CHOICE_REFRESH.free - used);
      const cost = freeLeft > 0 ? 0 : CHOICE_REFRESH.cost; const affordable = cost === 0 || (this.host.player?.coins ?? 0) >= cost;
      const label = cost === 0 ? `换一批题目 · 免费（还剩 ${freeLeft} 次）` : affordable ? `换一批题目 · ${cost} 星` : '星星不够，先画这一题吧';
      p.button('refresh-choices', label, p.x(25), y + 232, p.span(342), 44, () => { void this.host.run(async () => {
        const value = await this.host.api<{ session: SessionView; player: Player; cost: number }>(`/turns/${turn.id}/choices`, 'POST', {});
        this.session = value.session; this.host.player = value.player;
        this.host.toast(value.cost ? `已换一批题目，消耗 ${value.cost} 颗星星。` : '已免费换一批题目。');
      }); }, 'secondary', !affordable, affordable ? 'refresh' : undefined);
      // 灵感航线：词卡下方的空白区里，一架纸飞机沿虚线慢慢滑翔（空间不足时不画）。
      const freeY = y + 288; const freeH = bottom - freeY - 24;
      if (freeH > 170) {
        const cy = freeY + freeH / 2;
        const p0 = { x: 72, y: cy + 26 }, p1 = { x: 195, y: cy - 64 }, p2 = { x: 318, y: cy + 6 };
        p.ctx.save(); p.ctx.globalAlpha = 0.45; p.ctx.strokeStyle = C.mute; p.ctx.lineWidth = 1.2; p.ctx.setLineDash([4, 6]); p.ctx.lineCap = 'round';
        p.ctx.beginPath(); p.ctx.moveTo(p0.x, p0.y); p.ctx.quadraticCurveTo(p1.x, p1.y, p2.x, p2.y); p.ctx.stroke(); p.ctx.restore();
        const tt = (now / 7000) % 1; const mt = 1 - tt;
        const px = mt * mt * p0.x + 2 * mt * tt * p1.x + tt * tt * p2.x;
        const py = mt * mt * p0.y + 2 * mt * tt * p1.y + tt * tt * p2.y;
        const dx = 2 * mt * (p1.x - p0.x) + 2 * tt * (p2.x - p1.x); const dy = 2 * mt * (p1.y - p0.y) + 2 * tt * (p2.y - p1.y);
        p.ctx.save(); p.ctx.globalAlpha = 0.7; p.plane(px, py, Math.atan2(dy, dx), 0.62, C.mute); p.ctx.restore();
      }
    } else {
      p.card(p.x(24), y, p.span(342), 245, 26);
      // 邮筒 + 沿椭圆航线循环的邮差纸飞机：等待不再是死屏。
      const t = (now / 2600) * Math.PI * 2;
      for (let k = 6; k >= 1; k--) {
        const tk = t - k * 0.16;
        p.ctx.save(); p.ctx.globalAlpha = 0.32 - k * 0.04;
        p.circle(this.host.W / 2 + Math.cos(tk) * 108, y + 62 + Math.sin(tk) * 24, Math.max(1.2, 3 - k * 0.3), C.mute);
        p.ctx.restore();
      }
      p.plane(this.host.W / 2 + Math.cos(t) * 108, y + 62 + Math.sin(t) * 24, Math.atan2(Math.cos(t) * 24, -Math.sin(t) * 108), 1.05, C.ink);
      p.mailbox(p.x(195), y + 116, 0.92);
      const title = me && ['drawing', 'rejected'].includes(turn.status) ? '你的画纸还在这里' : !me && turn.status === 'ready' ? '收到一幅新画！' : turn.status === 'reviewing' ? '画作正在整理中' : '给友友一点时间';
      p.text(title, p.x(195), y + 193, T.title, C.ink, '700', 'center');
      p.text(turn.status === 'reviewing' ? '整理完成后，好友就能看到。' : '不用同时在线，有空再接着玩。', p.x(195), y + 224, T.caption, C.mute, '400', 'center'); y += 265;
      if (me && ['drawing', 'rejected'].includes(turn.status)) {
        if (turn.reason) { p.wrap(turn.reason, p.x(28), y + 15, p.span(329), T.caption, C.orange); y += 55; }
        p.button('continue-drawing', '继续我的画  →', p.x(24), y, p.span(342), 48, () => this.draw());
      } else if (!me && turn.status === 'ready') {
        p.button('start-guess', '打开画作，猜猜看  →', p.x(24), y, p.span(342), 48, () => this.guess());
        // 手动回看入口：自动播放只播一次，错过之后还能从这里重看 TA 上一次是怎么猜的。
        const own = this.latestOwnReplay();
        if (own && y + 104 <= bottom) p.button('watch-guess-replay', `回看 TA 猜第 ${own.number} 张画纸的过程`, p.x(24), y + 58, p.span(342), 40, () => { void this.host.run(() => this.loadGuessReplay(own, 'session')); }, 'plain');
      }
      else {
        p.button('check-reply', '看看有没有新回音', p.x(24), y, p.span(342), 44, () => { void this.host.run(() => this.host.refresh()); }, 'secondary');
        // 等待好友时能看到与 TA 的全部历史画作：会话视图只带最近 12 幅，完整列表懒加载（最新已揭晓回合
        // 变了就说明历史有更新，重新拉）；加载完成前先用视图里已有的渲染。矮屏放不下整排时就不画。
        const key = `${s.id}:${s.history[0]?.id ?? '0'}`;
        if (this.historyNeed !== key) {
          this.historyNeed = key; this.historyPage = 0;
          if (s.history.length) void this.host.run(async () => {
            const items = (await this.host.api<{ items: PublicTurn[] }>(`/sessions/${s.id}/history`)).items;
            if (this.historyNeed === key) { this.historyItems = items; this.historyGot = key; }
          });
          else { this.historyItems = []; this.historyGot = key; }
        }
        const history = (this.historyGot === key ? this.historyItems : s.history).filter(item => item.drawing);
        const space = bottom - y - 65;
        if (history.length && space >= 110) {
          const thumb = Math.min(96, space - 40); const labelY = y + 69;
          const pages = Math.ceil(history.length / 3); this.historyPage = Math.min(this.historyPage, pages - 1);
          p.text(`与 TA 一起画过的画（共 ${history.length} 幅）`, p.x(25), labelY, T.body, C.ink, '600');
          // 每页 3 幅；页码按钮排在标题行右侧，不额外占高度。
          if (pages > 1) {
            p.button('history-prev', '‹', p.x(271), labelY - 19, 30, 26, () => { this.historyPage--; }, 'plain', this.historyPage <= 0);
            p.text(`${this.historyPage + 1} / ${pages}`, p.x(318), labelY - 6, T.note, C.mute, '400', 'center');
            p.button('history-next', '›', p.x(335), labelY - 19, 30, 26, () => { this.historyPage++; }, 'plain', this.historyPage >= pages - 1);
          }
          history.slice(this.historyPage * 3, this.historyPage * 3 + 3).forEach((item, i) => {
            const x = 25 + i * (thumb + 14); const iy = labelY + 16;
            // 拍立得照片墙：白边小卡 + 顶部胶带 + 手写字标注。
            p.box(x - 3, iy - 3, thumb + 6, thumb + 6, C.white, 4, undefined, 10);
            const thumbnail = this.thumbnailFor(item);
            if (thumbnail) this.host.ctx.drawImage(thumbnail, x, iy, thumb, thumb);
            p.tape(x + thumb / 2, iy - 6, (i % 2 ? 1 : -1) * 0.16, 30, 10);
            p.fit(`${item.artistId === this.host.player!.id ? '我' : 'TA'} · ${item.word ?? ''}`, x + thumb / 2, iy + thumb + 15, thumb + 10, T.tiny, C.mute, '400', 'center');
            p.hits.push({ id: `history-${item.id}`, label: `回看 ${item.word}`, x: x - 3, y: iy - 3, w: thumb + 6, h: thumb + 24, action: () => this.openArtwork(item, 'session') });
          });
        }
      }
    }
    p.button('session-actions', '会话管理', p.x(25), bottom, p.span(105), 36, () => { void this.host.run(async () => {
      const action = await this.host.platform.choose('会话管理', ['结束会话', '屏蔽友友', '举报内容']);
      if (action === 0 || action === 1) {
        if (await this.host.platform.confirm(action === 1 ? '屏蔽这位友友？' : '结束这段会话？', '当前会话将关闭，已完成的画作仍在作品集中。')) { await this.host.api(`/sessions/${s.id}/close`, 'POST', { block: action === 1 }); this.host.go('home'); await this.host.refresh(); }
      } else if (action === 2) {
        const reasons = ['不适当内容', '隐私问题', '直接写答案', '其他问题']; const reason = await this.host.platform.choose('希望我们查看什么？', reasons);
        if (reason !== null) { await this.host.api('/reports', 'POST', { sessionId: s.id, reason: reasons[reason] }); this.host.toast('已收到举报，我们会尽快查看。'); }
      }
    }); }, 'plain');
    p.button('history', '一起画过的画', p.x(237), bottom, p.span(129), 36, () => this.navigateGallery(), 'plain');
  }
  drawingScreen() {
    const p = this.host.paint; const h = this.host.H - this.host.top;
    this.host.header(this.practice ? '随手画画' : '轮到你画啦');
    if (this.practice) {
      p.text('灵感来了，就画下来。', p.x(195), 92, T.section, C.ink, '700', 'center');
      p.text(this.draftFailed ? '草稿暂未保存，请先保存为图片' : '作品自动保存在这台设备', p.x(195), 120, T.note, C.mute, '400', 'center');
    } else {
      const word = this.session!.current.word ?? '';
      p.ctx.font = `400 13px "PingFang SC", "Microsoft YaHei", sans-serif`;
      const labelW = p.ctx.measureText('画一个').width;
      p.ctx.font = `700 19px "PingFang SC", "Microsoft YaHei", sans-serif`;
      const wordW = p.ctx.measureText(word).width;
      const chipW = Math.min(300, labelW + 12 + wordW + 48);
      p.box(this.host.W / 2 - chipW / 2, 86, chipW, 42, C.white, 21, undefined, 10);
      p.tape(p.x(195), 88, 0.05, 42, 12);
      p.text('画一个', this.host.W / 2 - chipW / 2 + 24, 107.5, T.sub, C.mute);
      p.text(word, this.host.W / 2 - chipW / 2 + 36 + labelW, 108, T.glyph, C.accentDeep, '700');
    }
    const compact = h <= 600;
    // 抽屉三段常驻：颜色行、工具行、粗细滑块行（滑块固定在工具圆钮**下方**，C-015）。
    // 三者互不影响：点色点 / 油漆桶都不会改变滑块的显示。
    const gap = compact ? 8 : 10;
    const panelH = this.host.tablet ? 168 : Math.max(150, Math.min(180, h - 360));
    // 画板吃掉抽屉与底部提交按钮之外的剩余高度；0.96 的余量保证矮屏也放得下三段。
    const boardSize = this.host.tablet ? Math.max(120, Math.min(this.host.W - 64, h - 143 - panelH - 85 - gap - 16)) : Math.max(120, Math.min(330, h - 420, (h - 85 - gap - panelH - 143) * 0.96));
    this.board = { x: (this.host.W - boardSize) / 2, y: 143, size: boardSize };
    // panelY 必须读刚更新的 this.board：读上一帧的 board 会让抽屉与画板差一帧、错开一整块画板。
    const panelY = this.board.y + this.board.size + gap;
    this.panel = { y: panelY, height: panelH };
    const b = this.board;
    if (this.bufferRevision !== this.editor.revision) { this.editorRenderer.render(this.buffer.getContext('2d')!, this.editor); this.bufferRevision = this.editor.revision; }
    p.box(b.x - 5, b.y - 5, b.size + 10, b.size + 10, C.white, 16, undefined, 18);
    this.view.render(this.host.ctx, b, this.buffer);
    // 直线笔头：预览不写进文档，只画在视口里；吸附后画实线并补上两端的端点。
    this.drawLinePreview();
    // 画纸两角贴上和纸胶带（贴在白框上，不碰画作区域）；矮屏副标题与画纸距离太近时不贴。
    if (!compact) {
      p.tape(b.x + 34, b.y - 10, -0.32, 46);
      p.tape(b.x + b.size - 34, b.y - 10, 0.32, 46);
    }
    if (this.view.changed) p.button('reset-zoom', '恢复默认画布', p.x(24), b.y - 43, p.span(118), 32, () => this.view.reset(), 'plain');
    const tools = renderDrawingTools(this.host, this, panelY, panelH, b.size, {
      canUndo: this.editor.canUndo, canRedo: this.editor.canRedo, background: this.editor.document.background,sliding:this.brushSliding!==null,
      palette: () => { this.saveDraft(); this.host.studio.openShop('palette'); },
      undo: () => { this.editor.undo(); this.saveDraft(false); }, redo: () => { this.editor.redo(); this.saveDraft(false); },
      cancelLine: () => this.cancelLine(),
      clear: () => { void this.host.run(async () => { if (await this.host.platform.confirm('换一张空白画纸？', '清空后仍可通过撤销找回上一幅画。')) { this.editor.clear(); this.saveDraft(false); } }); },
      fill: () => { try { this.editor.fill(this.color); this.eraser = false; this.saveDraft(false); this.host.toast('背景已经换成这个颜色，撤销可以还原。'); } catch (error) { this.host.toast((error as Error).message); } },
    });
    this.sliderY = tools.sliderY; this.sliderRight = tools.sliderRight;
    this.brushPanelRect();
    if (this.brushPanel) this.brushCatalog(panelH < 175);
    p.button('send-drawing', this.practice ? '保存为图片' : '画好了，寄给好友', p.x(24), h - 85, p.span(342), 51, () => {
      // 在进入任何异步流程前直接调用，确保微信识别为按钮手势触发。
      const subscribePromise = !this.practice ? this.host.platform.requestSubscribe?.() : undefined;
      this.saveDraft(); this.host.platform.vibrate(); void this.host.run(async () => {
        if (this.practice) { await this.exportDrawing(this.editor.document); return; }
        // 服务端 schema 要求 strokes 至少 1 条：只填了背景就提交会被 400 拒绝，这里提前给出明确原因。
        if (!this.editor.document.strokes.length) { this.host.toast('在背景上再画一笔才能寄出去。'); return; }
        const subscribed = subscribePromise ? await subscribePromise : false;
        // 必须在按钮点击产生的同步手势链中申请订阅；放到网络提交之后会被微信静默忽略。
        const turnId = this.session!.current.id;
        let key = this.host.platform.get(`submit:${turnId}`, ''); if (!key) { key = requestId(); this.host.platform.set(`submit:${turnId}`, key); }
        this.session = await this.host.api(`/turns/${turnId}/drawing`, 'POST', this.editor.snapshot(), key);
        if (subscribed) await this.host.api('/subscriptions', 'POST', { templateId: 'uSQvXX-IsTNqJS4TXIbBDuLb8X8fcjrqxXsWU_GlPaY' }, requestId());
        removeDraft(this.host.platform, `draft:${turnId}`); this.unsavedDrafts.delete(`draft:${turnId}`); this.host.platform.remove(`submit:${turnId}`);
        this.host.screen = 'session'; this.mailAt = Date.now(); this.host.toast('画作已寄出，等一个回音。');
      });
    // 空画布才置灰；练习模式允许导出只有背景的底图，回合提交则在上面的动作里给出提示。
    }, 'primary', !this.editor.document.strokes.length && !this.editor.document.background, this.practice ? 'download' : 'send');
  }
  /** 两种画猜共用笔头目录，底边始终贴着抽屉顶边，矮屏向上展开。 */
  brushPanelRect() {
    this.brushPanelBox = brushCatalogBox(this.board.y, this.panel.y);
    return this.brushPanelBox;
  }
  brushCatalog(_tight: boolean) {
    renderBrushCatalog(this.host, this, this.brushPanelRect(), this.board.size, brush => this.selectBrush(brush));
  }
  /**
   * 笔头小样：不写文字也能一眼看出这支笔的差别（半透明画两条深浅、镂空画圆环、蜡笔带颗粒）。
   * `brush` 必须由调用方指定——工具行圆钮传当前笔头，目录里每一格传它自己的笔头；
   * 以前这里读的是「当前笔头」，导致选中某支笔后所有格子都变成那支笔的小样（v0.0.103 的显示缺陷）。
   */
  drawBrushSample(brush: StrokeBrush, cx: number, cy: number, width: number, brushWidth: number) {
    drawBrushSample(this.host.ctx, brush, cx, cy, width, brushWidth, this.eraser ? C.mute : this.color, this.shape, this.shapeFilled);
  }
  /** 画布上的拖动预览：直线（虚线 = 还会吸直、实线 = 已吸住）与形状（圆 / 方 / 三角）都画在这里。 */
  drawLinePreview() {
    if (!this.lineFrom || !this.lineTo || this.brushPanel) return;
    renderDragPreview(this.host, this, this.board, this.view, this.lineFrom, this.lineTo, this.lineOptions);
  }

  /** 预览与目录小样都要知道「现在这支笔长什么样」：橡皮时按钢笔画。 */
  editorBrush(): StrokeBrush { return this.eraser ? 'pen' : this.brush; }
  /** 目录里每格的小样按各自笔头的默认参数画，不受当前粗细 / 形状影响（同一支笔的格子才跟随）。 */
  sampleShape(brush: StrokeBrush): StrokeShape { return brush === 'shape' ? this.shape : 'circle'; }
  sampleFilled(brush: StrokeBrush): boolean { return brush === 'shape' && this.shapeFilled; }
  drawReplay(drawing: Drawing, now: number, x: number, y: number, size: number, progress = true) {
    const end = replayDuration(drawing); const t = this.replayFinished ? Infinity : this.replayPausedPosition ?? (now - this.replayStarted) * this.replaySpeed;
    if (this.replayDrawing !== drawing || !this.replayFinished || !this.replayRenderedFull) {
      renderDrawingProgress(this.buffer.getContext('2d')!, drawing, 1000, t); this.replayDrawing = drawing; this.replayRenderedFull = t >= end;
    }
    this.host.paint.box(x - 4, y - 4, size + 8, size + 8, C.white, 14, undefined, 16);
    this.host.ctx.drawImage(this.buffer, x, y, size, size);
    if (t >= end) this.replayFinished = true;
    // 回放进度画成一条铅笔线，笔头跟着进度走；结果页画作已完整展示，不需要进度线。
    if (!progress) return;
    const ratio = Math.min(1, end ? t / end : 1);
    this.host.paint.box(x, y + size + 6, size, 3, C.line, 1);
    this.host.paint.box(x, y + size + 6, size * ratio, 3, C.orange, 1);
    if (!this.replayFinished) this.host.paint.circle(x + size * ratio, y + size + 7.5, 3.5, C.orange);
  }
  guessScreen(now: number) {
    const p = this.host.paint; const t = this.session!.current; const h = this.host.H - this.host.top;
    const banks = t.banks ?? []; const answer = [...this.answer];
    this.host.header('猜猜 TA 的小心思'); p.text(`${t.category} · ${t.letterCount} 个字`, p.x(26), 87, T.sub, C.mute);
    // 竖向布局按可用高度分三档：正常 / 紧凑（矮手机）/ 微型（iPad 缩放坐标）。画板吃剩余空间，
    // 答案格是猜词的焦点，正常档给到 48px；候选字块、控制行随之收放，保证与底部按钮恒留 8px 不重叠。
    const micro = h < 560; const compact = !micro && h < 680;
    const slotH = micro ? 32 : compact ? 38 : 48; const tileH = this.host.tablet ? 44 : micro ? 30 : compact ? 34 : 42;
    const ctrlH = this.host.tablet ? 44 : micro ? 28 : 32; const boardGap = micro ? 10 : compact ? 12 : 19;
    const slotTop = ctrlH + (micro ? 10 : compact ? 10 : 14); const slotGap = micro ? 10 : compact ? 14 : 16; const labelGap = micro ? 12 : compact ? 14 : 18;
    const size = Math.max(micro ? 84 : 110, Math.min(this.host.W - 48, h - (this.host.tablet ? 505 : micro ? 416 : compact ? 442 : 485))); this.drawReplay(t.drawing!, now, (this.host.W - size) / 2, 111, size);
    const y = 111 + size + boardGap;
    p.button('replay', '重播', p.x(25), y, p.span(94), ctrlH, () => this.replay(), 'plain', false, 'refresh');
    p.button('speed', this.replaySpeedLabel(), p.x(145), y, p.span(94), ctrlH, () => {
      const progress = this.guessDrawingPosition(Date.now());
      this.replaySpeed = this.replaySpeed === 1 ? 3 : 1; this.replayStarted = Date.now() - progress / this.replaySpeed;
      this.logGuessView();
    }, 'plain');
    p.button('skip-replay', '看完整画作', p.x(260), y, p.span(106), ctrlH, () => this.finishReplay(), 'plain');
    // 答案字格：同时承担“自己输入”的入口；单字题目没有候选字，只能走这里。
    const slotY = y + slotTop; const total = Math.max(t.letterCount ?? 0, answer.length, 1);
    const slotW = Math.min(46, 340 / total); const slotX = this.host.W / 2 - total * slotW / 2;
    for (let i = 0; i < total; i++) {
      const filled = i < answer.length; const active = i === answer.length;
      const sx = slotX + i * slotW + 2;
      // 刚填上的那一格轻轻弹跳一下（幅度 12%，克制不刻意），像字被按进信纸。
      const popAge = now - this.pickAt;
      const pop = filled && i === answer.length - 1 && popAge < 180 ? 1 + 0.12 * Math.sin((popAge / 180) * Math.PI) : 1;
      p.ctx.save(); p.ctx.translate(sx + (slotW - 4) / 2, slotY + slotH / 2); p.ctx.scale(pop, pop); p.ctx.translate(-(sx + (slotW - 4) / 2), -(slotY + slotH / 2));
      p.box(sx, slotY, slotW - 4, slotH, filled ? C.tint : C.white, 12, active ? C.orange : C.line, filled ? 0 : 10);
      if (!filled) p.dashed(sx + 6, slotY + slotH - 9, sx + slotW - 10, slotY + slotH - 9, C.line, 1.4, [3, 3]);
      // 待填写的当前格里画一支闪烁的书写光标，像正在信纸上写字。
      if (active && !this.typed && Math.floor(now / 530) % 2 === 0) {
        p.ctx.save(); p.ctx.strokeStyle = C.orange; p.ctx.lineWidth = 2; p.ctx.lineCap = 'round';
        p.ctx.beginPath(); p.ctx.moveTo(sx + (slotW - 4) / 2, slotY + 9); p.ctx.lineTo(sx + (slotW - 4) / 2, slotY + slotH - 9); p.ctx.stroke(); p.ctx.restore();
      }
      if (filled) p.text(answer[i], slotX + i * slotW + slotW / 2, slotY + slotH / 2, slotH >= 44 ? T.title : T.glyph, C.accentDeep, '700', 'center');
      p.ctx.restore();
    }
    p.hits.push({ id: 'answer-input', label: '自己输入答案', x: p.x(24), y: slotY, w: p.span(342), h: slotH, action: () => { void this.host.run(async () => {
      const value = await this.host.platform.prompt('你猜是什么？', t.letterCount === 1 ? '这道题只有一个字，写出你的答案' : '输入答案', this.answer);
      if (value !== null) {
        const text = value.trim();
        if (!text) this.clearAnswer();
        else { this.answer = text; this.picks = []; this.typed = true; this.logGuess('type', { text }); }
      }
    }); } });
    const labelY = slotY + slotH + slotGap; const tilesY = labelY + labelGap;
    if (!banks.length) {
      p.card(p.x(24), labelY - 16, p.span(342), 76, 20);
      p.text(t.letterCount === 1 ? '这道题只有一个字，没有候选字' : '这一回合没有候选字', p.x(195), labelY + 8, T.bodyStrong, C.ink, '600', 'center');
      p.text('点上面的字格，自己写下答案。', p.x(195), labelY + 37, T.caption, C.mute, '400', 'center');
    } else if (this.picks.length >= banks.length) {
      p.card(p.x(24), labelY - 16, p.span(342), 76, 20);
      p.text('已经拼好了', p.x(195), labelY + 8, T.bodyStrong, C.ink, '600', 'center');
      p.text('确认无误就点下面的「就是这个！」，点字格可以重选。', p.x(195), labelY + 37, T.caption, C.mute, '400', 'center');
    } else {
      const stage = this.picks.length; const batch = banks[stage];
      p.text(`第 ${stage + 1} / ${banks.length} 个字 · 这 ${batch.length} 个候选里只有一个是对的`, p.x(26), labelY, T.caption, C.mute);
      const cell = (this.host.W - 50) / 7;
      batch.forEach((char, index) => {
        // 活字印章字块：每换一批从右侧交错滑入。
        const slideAge = now - this.batchAt - index * 14;
        const k = Math.max(0, Math.min(1, slideAge / 180));
        const dx = (1 - k) * 36;
        const x = 26 + (index % 7) * cell; const ty = tilesY + Math.floor(index / 7) * (tileH + 4);
        p.ctx.save(); p.ctx.globalAlpha = k;
        p.box(x + dx, ty, cell - 6, tileH, C.cream, 10, C.line, 8);
        p.text(char, x + dx + (cell - 6) / 2, ty + tileH / 2, T.glyph, C.ink, '700', 'center');
        p.ctx.restore();
        p.hits.push({ id: `letter-${stage}-${index}`, label: `候选 ${char}`, x, y: ty, w: cell - 6, h: tileH, action: () => this.pickLetter(batch, index) });
      });
    }
    p.button('reset-answer', '重新选字', p.x(25), h - 131, p.span(103), 32, () => this.clearAnswer(), 'plain');
    p.button('hint', t.hintUsed ? '提示已使用' : banks.length ? '移除干扰字' : '无候选字', p.x(143), h - 131, p.span(108), 32, () => { void this.host.run(async () => { this.session = await this.host.api(`/turns/${t.id}/hint`, 'POST', {}); this.logGuess('hint'); this.clearAnswer(); }); }, 'plain', t.hintUsed || !banks.length);
    p.button('give-up', '揭晓答案', p.x(267), h - 131, p.span(99), 32, () => this.submitGuess(true), 'plain');
    // 逐字作答时必须选满全部位置才能提交；自己输入的答案按非空判断。
    p.button('guess-submit', '就是这个！', p.x(25), h - 85, p.span(340), 52, () => this.submitGuess(), 'primary', !this.answer || (!this.typed && banks.length > 0 && this.picks.length < banks.length));
  }
  // —— 猜题回放：把对方猜这幅画的全过程按事件流重放一遍（含选中、取消、提示与提交） ——
  /** 直接使用原始时间节点：不压缩思考、不拉长快操作，也不添加首尾停顿。 */
  processPlan() {
    const proc = this.guessProcess!;
    if (this.processAtFor === proc.id && this.processAt.length === proc.events.length) return { at: this.processAt, duration: this.processDuration };
    const at = proc.events.map(event => event.t);
    this.processAt = at; this.processAtFor = proc.id; this.processDuration = at[at.length - 1] ?? 0;
    return { at, duration: this.processDuration };
  }
  /** 回放时钟：暂停后位置停住，倍速只影响之后推进的速度；播到结尾自动停。 */
  processClock(now: number) {
    const plan = this.processPlan();
    const elapsed = Math.max(0, Math.min(plan.duration, this.processElapsed + (this.processPlaying ? Math.max(0, now - this.processStartedAt) * this.processSpeed : 0)));
    if (this.processPlaying && elapsed >= plan.duration) { this.processPlaying = false; this.processElapsed = plan.duration; }
    return { elapsed, duration: plan.duration };
  }
  /** 把事件流折成某一时刻的画面：答案格内容、当前候选批次、是否用过提示、提交过几次。 */
  processState(elapsed: number, through = Number.POSITIVE_INFINITY) {
    const proc = this.guessProcess!; const plan = this.processPlan();
    const state = {
      picks: [] as number[], text: '', typed: false, hinted: false, attempts: 0,
      last: -1, age: Number.POSITIVE_INFINITY, stageAt: 0,
      verdict: null as null | { correct: boolean; giveUp: boolean },
    };
    const batches = (hinted: boolean) => (hinted && proc.banks.length ? proc.banks : proc.banksFull);
    proc.events.forEach((event, index) => {
      if (plan.at[index] > elapsed || index > through) return;
      state.last = index; state.age = elapsed - plan.at[index];
      if (event.kind === 'pick') {
        state.stageAt = plan.at[index];
        state.picks[event.slot ?? state.picks.length] = event.index ?? 0; state.typed = false;
        const banks = batches(state.hinted);
        state.text = state.picks.map((tile, position) => banks[position]?.[tile] ?? '').join('');
      } else if (event.kind === 'clear') {
        state.stageAt = plan.at[index]; state.picks = []; state.text = ''; state.typed = false;
      } else if (event.kind === 'type') {
        state.picks = []; state.text = event.text ?? ''; state.typed = true;
      } else if (event.kind === 'hint') {
        state.stageAt = plan.at[index]; state.hinted = true;
      } else {
        const attempt = state.attempts; state.attempts++;
        // 提交内容以当时真正发送的文本为准，不能依赖此前选字是否完整记录。
        state.text = event.text ?? state.text;
        state.verdict = { correct: Boolean(proc.guesses[attempt]?.correct), giveUp: Boolean(event.giveUp) };
      }
    });
    // 提交过但后面还有事件 = 那次没猜中，只是中间尝试：只在短时间内提示，不当作最终结果。
    const submitted = state.last >= 0 && proc.events[state.last].kind === 'submit' ? state.last : -1;
    return { ...state, submitted, settled: submitted === proc.events.length - 1 };
  }
  processTime(ms: number) {
    const value = Math.max(0, Math.floor(ms));
    const minutes = Math.floor(value / 60000).toString().padStart(2, '0');
    const seconds = (Math.floor(value / 1000) % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}.${(value % 1000).toString().padStart(3, '0')}`;
  }
  stepProcess(direction: -1 | 1) {
    const { elapsed } = this.processClock(Date.now());
    const current = this.processStep ?? this.processState(elapsed).last;
    this.processStep = Math.max(-1, Math.min(this.guessProcess!.events.length - 1, current + direction));
    this.processElapsed = this.processStep < 0 ? 0 : this.processPlan().at[this.processStep];
    this.processPlaying = false;
  }
  processAction(state: ReturnType<DrawGuessClient['processState']>) {
    const proc = this.guessProcess!; const event = proc.events[state.last];
    if (!event) return '开始猜题，等待第一次操作';
    let action: string;
    if (event.kind === 'pick') {
      const banks = state.hinted && proc.banks.length ? proc.banks : proc.banksFull;
      action = `选中「${banks[event.slot ?? state.picks.length - 1]?.[event.index ?? 0] ?? ''}」`;
    } else if (event.kind === 'clear') action = '清空答案，重新选择';
    else if (event.kind === 'type') action = `输入「${event.text ?? ''}」`;
    else if (event.kind === 'hint') action = '使用提示，移除干扰字';
    else action = event.giveUp ? '揭晓答案' : `提交「${event.text ?? state.text}」 · ${state.verdict?.correct ? '猜中' : '未猜中'}`;
    return `${this.processTime(event.t)}  ${action}`;
  }
  processView(elapsed: number) {
    const drawing = this.processTurn?.drawing;
    return guessViewAt(this.guessProcess?.viewEvents, elapsed, drawing ? replayDuration(drawing) : 0);
  }
  drawProcess(drawing: Drawing, position: number, x: number, y: number, size: number) {
    if (this.processDrawing !== drawing || this.processDrawingPosition !== position) {
      renderDrawingProgress(this.buffer.getContext('2d')!, drawing, 1000, position);
      this.processDrawing = drawing; this.processDrawingPosition = position;
      this.replayDrawing = null; this.replayRenderedFull = false;
    }
    this.host.paint.box(x - 4, y - 4, size + 8, size + 8, C.white, 14, undefined, 16);
    this.host.ctx.drawImage(this.buffer, x, y, size, size);
  }
  replayScreen(now: number) {
    const p = this.host.paint; const proc = this.guessProcess!; const tune = this.processTurn; const h = this.host.H - this.host.top;
    const { elapsed, duration } = this.processClock(now);
    const state = this.processState(elapsed, this.processStep ?? Infinity);
    const banks = state.hinted && proc.banks.length ? proc.banks : proc.banksFull;
    const stage = Math.min(state.picks.length, Math.max(0, banks.length - 1));
    const batch = banks[stage] ?? [];
    this.host.header('TA 是怎么猜的', `第 ${proc.number} 张画纸 · 题目「${proc.answer}」 · ${proc.letterCount} 个字`);
    const view = this.processView(elapsed);
    p.text(view.recorded ? '画作与答题同步回放' : '旧记录：画作按默认节奏播放', p.x(195), 99, T.tiny, C.mute, '400', 'center');
    // 版面自下而上排：底部按钮 → 控制行 → 时间轴 → 候选字块 → 标签 → 答案格 → 画作吃剩余空间。
    const micro = h < 560; const compact = !micro && h < 680;
    const slotH = micro ? 32 : compact ? 38 : 48; const tileH = this.host.tablet ? 44 : micro ? 30 : compact ? 34 : 42;
    const controlH = micro ? 28 : 32; const bottomH = micro ? 40 : 44;
    const bottomY = h - bottomH - 14; const controlY = bottomY - controlH - 12;
    const rows = batch.length ? Math.ceil(batch.length / 7) : 0;
    const tilesH = rows ? rows * tileH + (rows - 1) * 4 : 0;
    const timelineY = controlY - 47;
    const tilesY = timelineY - 16 - tilesH;
    const labelY = tilesY - 16;
    const slotY = labelY - (micro ? 24 : 30) - slotH;
    const size = Math.max(micro ? 84 : 110, Math.min(this.host.W - 48, slotY - 16 - 111));
    this.processBoard = { x: (this.host.W - size) / 2, y: 111, size };
    this.processTiles = { y: tilesY, height: tilesH };
    if (tune?.drawing) this.drawProcess(tune.drawing, view.position, (this.host.W - size) / 2, 111, size);
    else { p.box((this.host.W - size) / 2, 111, size, size, C.tint, 16); p.text('画作已下架', p.x(195), 111 + size / 2, T.sub, C.mute, '400', 'center'); }
    // 答案格：逐字填上的过程就是「选了哪些字」；取消选择后格子会一起空掉。
    const total = Math.max(proc.letterCount, state.text.length, 1);
    const slotW = Math.min(46, 340 / total); const slotX = this.host.W / 2 - total * slotW / 2;
    for (let i = 0; i < total; i++) {
      const filled = i < state.text.length; const active = i === state.text.length;
      const sx = slotX + i * slotW + 2;
      const popAge = state.last >= 0 && proc.events[state.last].kind === 'pick' ? state.age : Infinity;
      const pop = filled && i === state.text.length - 1 && popAge < 180 ? 1 + 0.12 * Math.sin((popAge / 180) * Math.PI) : 1;
      p.ctx.save(); p.ctx.translate(sx + (slotW - 4) / 2, slotY + slotH / 2); p.ctx.scale(pop, pop); p.ctx.translate(-(sx + (slotW - 4) / 2), -(slotY + slotH / 2));
      p.box(sx, slotY, slotW - 4, slotH, filled ? C.tint : C.white, 12, active ? C.orange : C.line, filled ? 0 : 10);
      if (!filled) p.dashed(sx + 6, slotY + slotH - 9, sx + slotW - 10, slotY + slotH - 9, C.line, 1.4, [3, 3]);
      if (filled) p.text(state.text[i], slotX + i * slotW + slotW / 2, slotY + slotH / 2, slotH >= 44 ? T.title : T.glyph, C.accentDeep, '700', 'center');
      p.ctx.restore();
    }
    // 当前操作保留原始时间点；倍速只推进播放时钟，不改动这里的时间与内容。
    const lastEvent = state.last >= 0 ? proc.events[state.last] : undefined;
    const action = this.processAction(state);
    p.fit(action, p.x(26), labelY, p.span(338), T.caption, state.verdict?.correct && state.submitted >= 0 ? C.accentDeep : C.ink, '600');
    // 长答案可以暂停展开；内容仍是这一刻已发生的操作。
    p.hits.push({ id: 'process-action', label: action, x: p.x(24), y: labelY - 12, w: p.span(342), h: 24, action: () => {
      this.processElapsed = this.processClock(Date.now()).elapsed; this.processPlaying = false;
      void this.host.run(() => this.host.platform.confirm('操作时间与内容', action).then(() => {}));
    } });
    // 候选字块：和猜词页一样两行铺满，被选中的那一格即时高亮（回放里能看到 TA 点了哪个字）。
    if (rows) {
      const cell = (this.host.W - 50) / 7;
      batch.forEach((char, index) => {
        const slideAge = this.processPlaying ? elapsed - state.stageAt - index * 14 : Infinity;
        const k = Math.max(0, Math.min(1, slideAge / 180));
        const dx = (1 - k) * 36;
        const x = 26 + (index % 7) * cell; const ty = tilesY + Math.floor(index / 7) * (tileH + 4);
        const picked = lastEvent?.kind === 'pick' && (lastEvent.slot ?? state.picks.length - 1) === stage && lastEvent.index === index && state.age < 520;
        p.ctx.save(); p.ctx.globalAlpha = k;
        p.box(x + dx, ty, cell - 6, tileH, picked ? C.accent : C.cream, 10, picked ? C.accent : C.line, 8);
        p.text(char, x + dx + (cell - 6) / 2, ty + tileH / 2, T.glyph, picked ? C.white : C.ink, '700', 'center');
        p.ctx.restore();
      });
    }
    // 时间轴：整段过程的进度；标签行下方一条细线，右侧小圆点跟着走。
    const ratio = duration ? Math.min(1, elapsed / duration) : 1;
    p.button('process-prev', '上一步', p.x(25), timelineY, 66, 32, () => this.stepProcess(-1), 'plain', state.last < 0);
    p.text('原始经过时间', p.x(195), timelineY + 5, T.tiny, C.mute, '400', 'center');
    p.fit(`${this.processTime(elapsed)} / ${this.processTime(duration)}`, p.x(195), timelineY + 22, p.span(196), T.note, C.ink, '600', 'center');
    p.button('process-next', '下一步', p.x(299), timelineY, 66, 32, () => this.stepProcess(1), 'plain', state.last >= proc.events.length - 1);
    p.box(p.x(25), controlY - 9, p.span(340), 3, C.line, 1);
    p.box(p.x(25), controlY - 9, this.host.span(340) * ratio, 3, C.orange, 1);
    if (elapsed < duration) p.circle(25 + this.host.span(340) * ratio, controlY - 7.5, 3, C.orange);
    const ended = elapsed >= duration && state.last >= proc.events.length - 1;
    p.button('process-play', this.processPlaying ? '暂停' : ended ? '已播完' : '播放', p.x(25), controlY, p.span(94), controlH, () => {
      if (this.processPlaying) { this.processElapsed = this.processClock(Date.now()).elapsed; this.processPlaying = false; return; }
      this.processStartedAt = Date.now(); this.processPlaying = true; this.processStep = null;
    }, 'plain', !this.processPlaying && ended);
    p.button('process-speed', `${this.processSpeed} 倍速`, p.x(145), controlY, p.span(94), controlH, () => {
      const current = this.processClock(Date.now()).elapsed;
      this.processElapsed = current; this.processStartedAt = Date.now();
      this.processSpeed = this.processSpeed === 1 ? 2 : this.processSpeed === 2 ? 4 : 1;
    }, 'plain');
    p.button('process-skip', '跳到结尾', p.x(260), controlY, p.span(106), controlH, () => { this.processElapsed = this.processPlan().duration; this.processPlaying = false; this.processStep = null; }, 'plain', ended);
    p.button('process-restart', '再看一遍', p.x(25), bottomY, p.span(164), bottomH, () => this.startProcess(), 'secondary', false, 'refresh');
    p.button('process-back', this.processReturn === 'gallery' ? '回到作品集' : this.processReturn === 'artwork' ? '回到画作' : '回到画纸', p.x(201), bottomY, p.span(164), bottomH, () => this.leaveProcess(), 'plain');
  }
  resultScreen() {
    const p = this.host.paint; const r = this.result!; const h = this.host.H - this.host.top; const now = Date.now();
    // 揭晓后当前回合已经翻到下一张画纸，刚才那幅画在历史里；两处都找不到时退回占位插画。
    const drawing = r.session.current.drawing ?? r.session.history[0]?.drawing ?? this.session?.current.drawing;
    this.host.header('一张有回音的画纸');
    // 揭晓后先看完整的画：写答案的位置要能看到画布，所以这里给画作留出整块空间。
    const size = Math.max(120, Math.min(this.host.W - 48, h - 466));
    let y = 108;
    if (drawing) {
      this.replayFinished = true; this.replayRenderedFull = false; this.replayDrawing = null;
      const ax = (this.host.W - size) / 2; const age = now - this.stampAt;
      // 画作装裱在信纸上，顶部两段胶带；彩纸从画后散落（先画彩纸再画画，永远不遮画面）。
      p.box(ax - 10, y - 10, size + 20, size + 20, C.cream, 8, undefined, 16);
      p.tape(ax + 32, y - 12, -0.3, 44); p.tape(ax + size - 32, y - 12, 0.3, 44);
      if (r.correct && age >= 0 && age < 1600) this.host.confetti(age, 195, y + 8);
      this.drawReplay(drawing, now, ax, y, size, false);
      // 邮戳盖章：猜中时「心意相通」带 overshoot 砸在右下角；未中是温柔的浅灰「再想想」。
      if (r.correct) {
        const k = Math.min(1, Math.max(0, age) / 240);
        const ease = 1 + 2.70158 * Math.pow(k - 1, 3) + 1.70158 * Math.pow(k - 1, 2);
        const scale = 1 + (1 - ease) * 0.65;
        p.ctx.save(); p.ctx.globalAlpha = Math.min(1, Math.max(0, age) / 120);
        p.ctx.translate(ax + size - 36, y + size - 40); p.ctx.scale(scale, scale); p.ctx.rotate(-0.18);
        this.host.resultStamp(0, 0, 40, '心意相通', C.accent);
        p.ctx.restore();
        // 盖章落地的尘环：戳定后一圈墨环扩散淡出，砸感更实。
        const impact = age - 240;
        if (impact >= 0 && impact < 220) {
          const ring = impact / 220;
          p.ctx.save(); p.ctx.globalAlpha = 0.35 * (1 - ring);
          p.ctx.strokeStyle = C.accent; p.ctx.lineWidth = 2;
          p.ctx.beginPath(); p.ctx.arc(ax + size - 36, y + size - 40, 40 + ring * 14, 0, Math.PI * 2); p.ctx.stroke();
          p.ctx.restore();
        }
      } else {
        p.ctx.save(); p.ctx.globalAlpha = Math.min(0.55, (Math.max(0, age) / 400) * 0.55);
        p.ctx.translate(ax + size - 36, y + size - 40); p.ctx.rotate(-0.18);
        this.host.resultStamp(0, 0, 38, '再想想', C.mute);
        p.ctx.restore();
      }
      y += size + 26;
    }
    else { p.doodle(p.x(195), y + 96, 1.1); y += 192 + 26; }
    p.text(r.correct ? '你懂 TA 的脑洞！' : '原来是这样呀', p.x(195), y + 8, T.result, C.ink, '700', 'center');
    p.text(r.answer!, p.x(195), y + 50, T.logo, C.accentDeep, '600', 'center');
    // 奖励行的星星画成手绘金星图标，不再依赖 ✦ 字符（部分机型缺字形）。
    if (r.correct) {
      const rewardText = `你们各获得 ${r.reward} 颗星星`;
      p.text(rewardText, p.x(195), y + 88, T.body, C.mute, '400', 'center');
      p.ctx.font = `400 ${T.body}px "PingFang SC", "Microsoft YaHei", sans-serif`;
      p.star(this.host.W / 2 + p.ctx.measureText(rewardText).width / 2 + 11, y + 88, 6, C.gold);
    } else {
      p.text('猜不出来也没关系，下张画纸继续。', p.x(195), y + 88, T.body, C.mute, '400', 'center');
    }
    p.button('next-turn', '下一步：轮到我画  →', p.x(25), h - 137, p.span(340), 52, () => { this.host.screen = 'session'; void this.host.run(() => this.host.refresh()); }, 'primary');
    p.button('result-home', '稍后再画，回到首页', p.x(25), h - 72, p.span(340), 44, () => this.host.home(), 'plain');
  }
  guideText() {
    return '双指可同时缩放（25%～400%）、移动和旋转画纸；两指全部抬起后再单指落笔。操作时显示比例和角度，点恢复默认画布可回到完整正向画纸。圆形工具从左到右：撤销退回上一笔、重做恢复、圆点按钮是「笔头」（点开最近与收藏，全部画笔可浏览36种笔刷、试画、调参数和保存预设；每支笔各自记参数，登录后跨设备同步，粗细滑块常驻在下方、颜色行不受影响、随时可选色）、橡皮擦除、清空整张画纸（可用撤销找回）、油漆桶把当前选中的颜色铺满整张画布作背景（可撤销还原；颜色行一直可选，随时能换填充色）。半透明叠在一起会变深；直线与形状都要拖动预览、抬手才落笔，直线接近水平 / 垂直 / 45° 会自动吸直；形状在调节页明确选择圆、方、三角和空心 / 实心；喷枪是颗粒喷雾、光晕在深色背景上像霓虹灯、毛笔慢慢画更粗、虚线是点线断线。橡皮对任何笔头都有效。手指滑出画布不会断笔，会贴着纸边继续画。';
  }
  constructor(readonly host: ClientHost) {
    this.buffer = host.platform.createCanvas(1000, 1000);
    this.editorRenderer = new DrawingEditorRenderer((width, height) => host.platform.createCanvas(width, height), 1000);
    setStrokeCanvasFactory((width, height) => host.platform.createCanvas(width, height));
  }
  async open(view: SessionView) {
    this.session = view; this.historyNeed = ''; const pending = this.pendingReplay();
    if (pending) await this.loadGuessReplay(pending, 'session'); else this.host.go('session');
  }
  dispose() {
    if (this.draftTimer !== undefined) clearTimeout(this.draftTimer);
    this.draftTimer = undefined; this.unsavedDrafts.clear();
    this.guessRecords.clear();
  }
  resize() {
    this.view.clearGesture(); this.gestureConsumed = false; this.pendingTouchHit = null;
    this.editor.end(); this.cancelLine(); this.pointerId = null; this.brushSliding = null;
    if (this.host.screen === 'draw') this.saveDraft();
  }
  pause() {
    if (this.host.screen === 'replay' && this.guessProcess) { this.processElapsed = this.processClock(Date.now()).elapsed; this.processPlaying = false; }
    if (this.host.screen === 'guess' && this.replayPausedPosition === null) {
      this.replayPausedPosition = this.guessDrawingPosition(Date.now()); this.logGuessView();
    }
    this.view.clearGesture(); this.gestureConsumed = false; this.pendingTouchHit = null; this.editor.end(); this.cancelLine(); this.pointerId = null; this.saveDraft(); this.brushSliding = null; this.host.paint.pressedId = null;
  }
  resume() {
    if (this.host.screen !== 'guess' || this.replayPausedPosition === null) return;
    this.replayStarted = Date.now() - this.replayPausedPosition / this.replaySpeed;
    this.replayPausedPosition = null; this.logGuessView();
  }
}
